import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MoreVertical, 
  Clock, 
  Users, 
  ArrowRight,
  Plus,
  CheckSquare,
  BookOpen,
  GraduationCap,
  User,
  Mail,
  Calendar,
  TrendingUp,
  Award,
  FileText,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { useSync } from "../../contexts/SyncContext";
import { toast } from "sonner";
import { getStudentsForClass } from "../../data/mockStudents";
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function ClassesPage() {
  const navigate = useNavigate();
  const { addChange } = useSync();
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [activeTab, setActiveTab] = useState("primary");
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [primaryClasses, setPrimaryClasses] = useState([]);
  const [juniorClasses, setJuniorClasses] = useState([]);
  const [seniorClasses, setSeniorClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPrimarySchool, setIsPrimarySchool] = useState(false);
  const [classDetailsOpen, setClassDetailsOpen] = useState(false);
  const [classDetails, setClassDetails] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const currentUser = getCurrentUser();
  
  // Fetch teacher's actual classes from API
  React.useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        const classes = await apiService.getTeacherClasses();
        setTeacherClasses(classes);
        
        // Separate primary (1-6), junior (7-10) and senior (11-13) classes
        const primary = classes.filter(c => c.year <= 6).map(c => ({
          id: c.id,
          title: c.name,
          grade: `Year ${c.year}`,
          room: c.subject || 'General',
          gradient: 'from-green-500 to-green-600',
          studentCount: c.student_count || 0
        }));
        
        const junior = classes.filter(c => c.year >= 7 && c.year <= 10).map(c => ({
          id: c.id,
          title: c.name,
          grade: `Year ${c.year}`,
          room: c.subject,
          gradient: 'from-blue-500 to-blue-600',
          studentCount: c.student_count || 0
        }));
        
        const senior = classes.filter(c => c.year >= 11).map(c => ({
          id: c.id,
          title: c.name,
          grade: `Year ${c.year}`,
          room: c.subject,
          gradient: 'from-purple-500 to-purple-600',
          studentCount: c.student_count || 0
        }));
        
        setPrimaryClasses(primary);
        setJuniorClasses(junior);
        setSeniorClasses(senior);
        
        // Set initial tab based on available classes
        if (primary.length > 0) {
          setActiveTab('primary');
          setIsPrimarySchool(true);
        } else if (junior.length > 0) {
          setActiveTab('junior');
        } else if (senior.length > 0) {
          setActiveTab('senior');
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        toast.error('Failed to load classes');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClasses();
  }, []);

  const openAttendance = (cls) => {
    setSelectedClass(cls);
    // Use the real student lists if available
    const classStudents = getStudentsForClass(cls.title);
    setStudents(classStudents);
    setAttendanceOpen(true);
  };

  const openClassDetails = async (cls) => {
    setClassDetails(cls);
    setClassDetailsOpen(true);
    setLoadingDetails(true);
    
    try {
      // Fetch students for this class
      const students = await apiService.getClassStudents(cls.id);
      setClassStudents(students || []);
    } catch (error) {
      console.error('Error fetching class students:', error);
      // Fallback to mock data if API fails
      const mockStudents = getStudentsForClass(cls.title);
      setClassStudents(mockStudents || []);
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleStudentStatus = (id) => {
    setStudents(students.map(s => 
      s.id === id 
        ? { ...s, status: s.status === 'present' ? 'absent' : 'present' } 
        : s
    ));
  };

  const saveAttendance = () => {
    const record = {
      classId: selectedClass.id,
      className: selectedClass.title,
      date: new Date().toISOString(),
      students: students
    };
    
    addChange('attendance', record);
    setAttendanceOpen(false);
  };

  const ClassCard = ({ cls }) => (
    <Card className="group overflow-hidden border-none shadow-md hover:shadow-xl transition-all duration-300">
      <div className={`h-24 bg-gradient-to-br ${cls.gradient} p-6 relative`}>
        <div className="absolute top-4 right-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Edit Class Details</DropdownMenuItem>
              <DropdownMenuItem>Manage Students</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive">Archive Class</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Badge className="bg-white/20 hover:bg-white/30 text-white border-none backdrop-blur-sm">
          {cls.room}
        </Badge>
      </div>

      <CardContent className="pt-0 -mt-8 px-6">
        <div className="bg-card rounded-xl p-4 shadow-sm border mb-4">
          <h3 className="font-bold text-lg truncate" title={cls.title}>{cls.title}</h3>
          <p className="text-muted-foreground text-sm">{cls.grade}</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            {cls.time}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex -space-x-2">
              {[1,2,3,4].map(i => (
                <Avatar key={i} className="h-8 w-8 border-2 border-background">
                  <AvatarImage src={`https://randomuser.me/api/portraits/thumb/men/${(i*5) + 10}.jpg`} />
                  <AvatarFallback>S{i}</AvatarFallback>
                </Avatar>
              ))}
              <div className="h-8 w-8 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs font-medium">
                +{Math.max(0, (cls.studentCount || 0) - 4)}
              </div>
            </div>
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Users className="h-4 w-4" />
              {cls.studentCount || 0}
            </div>
          </div>

          <div className="bg-secondary/10 p-3 rounded-lg border border-secondary/20">
             <p className="text-xs font-semibold text-secondary-foreground uppercase tracking-wider mb-1">Up Next</p>
             <p className="text-sm font-medium text-foreground">{cls.nextTask}</p>
          </div>
        </div>
      </CardContent>

      <CardFooter className="p-6 pt-0 gap-2">
        <Button 
          className="flex-1 group-hover:bg-primary group-hover:text-primary-foreground transition-colors" 
          variant="outline"
          onClick={() => openClassDetails(cls)}
        >
          Enter
        </Button>
        <Button 
          variant="secondary" 
          size="icon" 
          className="shrink-0"
          onClick={() => {
            // Store class selection and navigate to Roll Call
            localStorage.setItem('selectedClass', JSON.stringify({
              id: cls.id,
              subject: cls.subject || cls.title?.split(' ')[cls.title?.split(' ').length - 1],
              year: cls.year || parseInt(cls.grade?.replace('Year ', '')),
              name: cls.title
            }));
            window.dispatchEvent(new CustomEvent('classSelectionChanged'));
            navigate('/dashboard/roll-call');
          }}
          title="Take Attendance"
        >
          <CheckSquare className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );

  const AddClassCard = () => (
    <button className="h-full min-h-[320px] rounded-xl border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 hover:bg-primary/5 flex flex-col items-center justify-center gap-4 transition-all group p-8 text-center">
      <div className="h-16 w-16 rounded-full bg-muted group-hover:bg-primary/10 flex items-center justify-center text-muted-foreground group-hover:text-primary transition-colors">
        <Plus className="h-8 w-8" />
      </div>
      <div>
        <h3 className="font-bold text-lg">Add New Class</h3>
        <p className="text-sm text-muted-foreground mt-1">Create a new class</p>
      </div>
    </button>
  );

  // Group classes by Year for display
  const renderClassGroup = (classes) => {
    // Group by grade
    const grouped = classes.reduce((acc, cls) => {
      if (!acc[cls.grade]) acc[cls.grade] = [];
      acc[cls.grade].push(cls);
      return acc;
    }, {});

    return Object.entries(grouped).map(([grade, gradeClasses]) => (
      <div key={grade} className="space-y-4 mb-8">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-2 w-2 rounded-full bg-primary/50"></div>
          <h3 className="text-lg font-semibold tracking-tight text-muted-foreground">{grade}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {gradeClasses.map(cls => <ClassCard key={cls.id} cls={cls} />)}
        </div>
      </div>
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-50 -m-6 p-6">
      <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Classes</h1>
          <p className="text-muted-foreground mt-1">
            {isPrimarySchool ? 'Manage your primary school classes.' : 'Manage your teaching schedule across all levels.'}
          </p>
        </div>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Quick Add
        </Button>
      </div>

      {/* Primary Level Section - Only shown for primary school teachers */}
      {isPrimarySchool && (
        <section className="space-y-6 bg-slate-50/50 dark:bg-slate-900/20 p-6 rounded-2xl border border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-4 border-b pb-4 border-green-100 dark:border-green-900/30">
             <div className="h-10 w-10 rounded-xl bg-green-100 flex items-center justify-center text-green-700">
               <BookOpen className="h-6 w-6" />
             </div>
             <div>
               <h2 className="text-2xl font-bold tracking-tight text-green-900 dark:text-green-100">Primary Classes</h2>
               <p className="text-sm text-green-600/80 dark:text-green-400/80 font-medium">Years 1 - 6</p>
             </div>
          </div>
          {loading ? <p className="text-center py-8">Loading classes...</p> : renderClassGroup(primaryClasses)}
          {primaryClasses.length === 0 && !loading && (
            <p className="text-center text-muted-foreground py-4">No classes assigned yet.</p>
          )}
          <AddClassCard />
        </section>
      )}

      {/* Junior Level Section - Only shown for secondary school teachers */}
      {!isPrimarySchool && (
        <section className="space-y-6 bg-slate-50/50 dark:bg-slate-900/20 p-6 rounded-2xl border border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-4 border-b pb-4 border-emerald-100 dark:border-emerald-900/30">
             <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700">
               <BookOpen className="h-6 w-6" />
             </div>
             <div>
               <h2 className="text-2xl font-bold tracking-tight text-emerald-900 dark:text-emerald-100">Junior Level</h2>
               <p className="text-sm text-emerald-600/80 dark:text-emerald-400/80 font-medium">Years 7 - 10</p>
             </div>
          </div>
          {loading ? <p className="text-center py-8">Loading classes...</p> : renderClassGroup(juniorClasses)}
          <AddClassCard />
        </section>
      )}

      {/* Senior Level Section - Only shown for secondary school teachers */}
      {!isPrimarySchool && (
        <section className="space-y-6 bg-slate-50/50 dark:bg-slate-900/20 p-6 rounded-2xl border border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-4 border-b pb-4 border-indigo-100 dark:border-indigo-900/30">
             <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-700">
               <GraduationCap className="h-6 w-6" />
             </div>
             <div>
               <h2 className="text-2xl font-bold tracking-tight text-indigo-900 dark:text-indigo-100">Senior Level</h2>
               <p className="text-sm text-indigo-600/80 dark:text-indigo-400/80 font-medium">Years 11 - 13</p>
             </div>
          </div>
          {loading ? <p className="text-center py-8">Loading classes...</p> : renderClassGroup(seniorClasses)}
          <AddClassCard />
        </section>
      )}

      {/* Attendance Modal */}
      <Dialog open={attendanceOpen} onOpenChange={setAttendanceOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Take Attendance</DialogTitle>
            <DialogDescription>
              {selectedClass?.title} • {new Date().toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[300px] overflow-y-auto">
            {students.map((student) => (
              <div key={student.id} className="flex items-center justify-between p-2 border rounded hover:bg-muted/50 cursor-pointer" onClick={() => toggleStudentStatus(student.id)}>
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>{student.name[0]}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium">{student.name}</span>
                </div>
                <Badge variant={student.status === 'present' ? "default" : "destructive"} className="cursor-pointer">
                  {student.status === 'present' ? 'Present' : 'Absent'}
                </Badge>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={saveAttendance}>Save Attendance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Class Details Modal */}
      <Dialog open={classDetailsOpen} onOpenChange={setClassDetailsOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${classDetails?.gradient || 'from-blue-500 to-blue-600'} flex items-center justify-center`}>
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-xl">{classDetails?.title}</span>
                <p className="text-sm font-normal text-muted-foreground">{classDetails?.grade} • {classDetails?.room}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Class Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{classStudents.length}</p>
                      <p className="text-xs text-muted-foreground">Students</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">--</p>
                      <p className="text-xs text-muted-foreground">Avg. Score</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">--</p>
                      <p className="text-xs text-muted-foreground">Attendance</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">--</p>
                      <p className="text-xs text-muted-foreground">Assessments</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Students List */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Student List
                </h3>
                {classStudents.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">No students enrolled in this class yet.</p>
                  </Card>
                ) : (
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Student Name</TableHead>
                          <TableHead>Student ID</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {classStudents.map((student, index) => (
                          <TableRow key={student.id || index}>
                            <TableCell className="font-medium text-muted-foreground">{index + 1}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                    {(student.name || student.full_name || 'S')[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium">{student.name || student.full_name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{student.student_id || student.id}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Active
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setClassDetailsOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setClassDetailsOpen(false);
              // Store class selection and navigate to Roll Call
              localStorage.setItem('selectedClass', JSON.stringify({
                id: classDetails.id,
                subject: classDetails.title?.split(' ')[classDetails.title?.split(' ').length - 1] || classDetails.subject,
                year: parseInt(classDetails.grade?.replace('Year ', '')) || classDetails.year,
                name: classDetails.title
              }));
              window.dispatchEvent(new CustomEvent('classSelectionChanged'));
              navigate('/dashboard/roll-call');
            }}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Take Attendance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
